import '.a{display:flex;}.ssr-plugin'
import '.ac{flex-direction:column;}.ssr-plugin'
import '.f{width:100%;}.ssr-plugin'
import '.bk{gap:8px;}.ssr-plugin'
import '.a7{position:relative;}.ssr-plugin'
import '.am{height:100%;}.ssr-plugin'
import '.fx{padding:0px;}.ssr-plugin'
import '.c{justify-content:center;}.ssr-plugin'
import '.d{align-items:center;}.ssr-plugin'
import '.a1{gap:4px;}.ssr-plugin'
import { SchemaTypeName } from '@based/sdk/schema'
import { Modal } from '../Modal/index.js'
import { useState, useEffect } from 'react'
import { colors } from '../../tokens/colors.js'
import { Button } from '../Button/index.js'
import { Icon } from '../Icon/index.js'
import { styled } from 'inlines'
import {
  useBasedSchema,
  useBasedQuery,
} from '../../providers/Endpoints/index.js'
import { Stack } from '../Stack/index.js'
import { Text } from '../Text/index.js'
import { Dialog } from '../Dialog/index.js'
import { ExpandedView } from './ExpandedView.js'
import { BasedFinder } from '../BasedFinder/index.js'
import { List } from '../List/index.js'
import { DragAndDropProvider } from '../../hooks/useDragAndDrop.js'
import { Badge } from '../Badge/index.js'
import { Media } from '../Media/index.js'
import { useOverlay } from '../../hooks/useOverlay.js'
import { ContextMenu } from '../ContextMenu/index.js'
import { prettyDate } from '@based/pretty-date'
import { InlineFileInput } from '../InlineFileInput/index.js'
import { radius } from '../../utils/radius.js'

export type ReferenceValue = Record<string, any> & { id: number | string }

export type ReferenceInputProps = {
  type: SchemaTypeName
  disabled?: boolean
  error?: boolean
  label?: string
  limit?: number
} & (
  | {
      variant: 'single'
      onChange: (value: ReferenceValue) => void
      value?: ReferenceValue
    }
  | {
      variant: 'multiple'
      onChange: (value: ReferenceValue[]) => void
      value?: ReferenceValue[]
      onOrderChange?: (draggedId: string, idx: number) => void
    }
)

export const ReferenceInput = (props: ReferenceInputProps) => {
  const { type, value, onChange, variant, disabled, error, limit, label } =
    props

  const newLimit = variant === 'single' ? 1 : limit

  const [select, setSelect] = useState<any>()
  const [selectedSet, setSelectedSet] = useState<Set<number>>()

  const [expandedView, setExpandedView] = useState(false)

  const { data: schema } = useBasedSchema()

  const [initData, setInitData] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)

  const typeSchema =
    schema?.[type || '_root'] || schema?.types?.[type || '']?.props || {}

  const include = Object.keys(typeSchema)

  const { data: queryData, checksum: queryChecksum } = useBasedQuery(
    isModalOpen ? { type, include } : null,
  )

  useEffect(() => {
    if (queryData && isModalOpen) {
      setInitData(queryData)
    }
  }, [queryData, isModalOpen])

  let height: string | number = 120
  if (value && value.length) {
    height = 'auto'
  }

  const setFromValueIds: Set<number> = new Set(value?.map((item) => item.id))

  const removeAllRefsDialog = useOverlay(Dialog, {
    title: `Remove all ${type}s from this field?`,
    description: `They will not be deleted from the workspace, only remove from this field.`,
    children: (
      <Stack gap={16}>
        <Button
          align="center"
          keyHint="Esc"
          variant="border"
          width="full"
          onClick={() => {
            Dialog.Close()
          }}
        >
          Cancel
        </Button>
        <Button
          keyHint="Enter"
          color="red"
          align="center"
          width="full"
          onClick={() => {
            onChange(null)
            Dialog.Close()
          }}
        >
          Remove
        </Button>
      </Stack>
    ),
  })

  return expandedView ? (
    <ExpandedView
      setExpandedView={setExpandedView}
      type={type}
      value={value}
      schema={schema}
    />
  ) : (
    <>
      <div className="a ac f bk"
      >
        {label && (
          <Text variant="subtext-regular" color="neutral60">
            {label}{' '}
            {variant === 'single' || limit
              ? `(${value?.length || 0} of ${newLimit})`
              : null}
          </Text>
        )}
        <styled.div
          style={{
            height,
            maxHeight: variant === 'multiple' ? 254 : 62,
            border:
              !value || value.length < 1
                ? `1px solid ${colors.neutral20Adjusted}`
                : '0px solid transparent',
            borderRadius: radius[20],
          }}
        >
          {value?.length > 0 ? (
            <DragAndDropProvider>
              <List
                style={{ maxHeight: 254 }}
                data={value as []}
                select={select}
                disabled={disabled}
                onSelectChange={setSelect}
                onRemove={(x) => {
                  if (disabled) {
                    return
                  }

                  const filteredVals = value.filter(
                    (val) => val?.id !== value[x]?.id,
                  )
                  onChange(variant === 'single' ? null : (filteredVals as any))
                }}
                fields={{
                  id: {
                    key: 'id',
                    render: ({ id }) => (
                      <Badge color="neutral-subtle">{'# ' + id}</Badge>
                    ),
                  },
                  picture: {
                    key: 'picture',
                    render: ({ picture }) => {
                      return picture ? (
                        <Media src={picture} size="small" type="image" />
                      ) : null
                    },
                  },
                  src: {
                    key: 'src',
                    render: ({ src }) => {
                      return src ? <Media src={src} size="small" /> : null
                    },
                  },
                  img: {
                    key: 'img',
                    render: ({ img }) => {
                      return img ? <Media src={img} size="small" /> : null
                    },
                  },
                  image: {
                    key: 'image',
                    render: ({ image }) => {
                      return image ? <Media src={image} size="small" /> : null
                    },
                  },
                  name: {
                    key: 'name',
                  },
                  title: {
                    key: 'title',
                  },
                  email: {
                    key: 'email',
                  },
                  startTime: {
                    key: 'starttime',
                    render: ({ startTime }) => {
                      return startTime ? (
                        <Text variant="subtext-mono">
                          {prettyDate(startTime, 'time-precise')}
                        </Text>
                      ) : null
                    },
                  },
                  completed: {
                    key: 'completed',
                    render: ({ completed }) =>
                      completed ? (
                        <Badge color="green-subtle" leadIcon="checkmark-normal">
                          Completed
                        </Badge>
                      ) : null,
                  },
                }}
              ></List>
            </DragAndDropProvider>
          ) : (
            <div className="a7 f am a fx c ac d a1"
            >
              {variant === 'multiple' && (
                <Icon variant="reference" color="neutral60" />
              )}
              <Text color="neutral60" variant="subtext-regular">
                {newLimit === undefined
                  ? `Add ${type}(s)`
                  : newLimit >= 1 || variant === 'multiple'
                    ? `Add up to ${newLimit} ${type}s`
                    : `Add a ${type}`}
              </Text>
            </div>
          )}
        </styled.div>

        {!disabled && (
          <Stack gap={8} justify="start">
            <Modal
              size="large"
              title={
                newLimit >= 1 || variant === 'multiple'
                  ? `Add up to ${newLimit} ${type}s`
                  : `Add a ${type}`
              }
              open={isModalOpen}
              onOpen={() => setIsModalOpen(true)}
              onClose={() => setIsModalOpen(false)}
            >
              <Modal.Trigger>
                <Button
                  leadIcon="add"
                  variant="border"
                  disabled={disabled || value?.length >= newLimit}
                  onClick={() => {
                    let ids = value?.map((item) => item.id)
                    const setje = new Set(ids)

                    if (value && value.length > 0) {
                      setSelectedSet(setje as Set<number>)
                    }
                  }}
                >
                  {`Add ${type}`}
                </Button>
              </Modal.Trigger>
              <Modal.Content>
                <Modal.Body>
                  <div className="am"
                  >
                    <BasedFinder
                      schemaType={type}
                      selectedRows={selectedSet}
                      onSelectedRowsChange={setSelectedSet}
                      onRowClick={(values) =>
                        setSelectedSet((prev) => {
                          const next = new Set(prev)
                          if (next.has(values.id)) {
                            next.delete(values.id)
                          } else {
                            next.add(values.id)
                          }
                          return next
                        })
                      }
                      disabledRows={setFromValueIds}
                      variant="modal"
                    >
                      <BasedFinder.Header
                        trailButton={
                          type === 'file' ? (
                            <InlineFileInput>
                              <Button variant="border">Upload file(s)</Button>
                            </InlineFileInput>
                          ) : null
                        }
                      />
                      <BasedFinder.Body />
                    </BasedFinder>
                  </div>
                </Modal.Body>
              </Modal.Content>
              <Modal.Footer>
                <Stack justify="end" gap={16}>
                  <Button variant="border" onClick={() => Modal.Close()}>
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      const selectedData = initData.filter((row) =>
                        selectedSet.has(row.id),
                      )
                      if (variant === 'single') {
                        if (selectedData.length > 0) {
                          onChange(selectedData[0])
                        }
                      } else {
                        const merged = [...(value || []), ...selectedData]
                        onChange(merged)
                      }

                      setSelectedSet(new Set())
                      Modal.Close()
                    }}
                    disabled={
                      selectedSet?.size === 0 || selectedSet?.size > newLimit
                    }
                  >
                    {selectedSet?.size <= 1
                      ? `Add ${type}`
                      : `Add ${selectedSet?.size} ${type}(s)`}
                  </Button>
                </Stack>
              </Modal.Footer>
            </Modal>
            {select?.length > 0 && (
              <Button
                leadIcon="remove"
                variant="border"
                onClick={() => {
                  const arr = select.map((item) => item.id)
                  const vals = [...(value as any)]

                  const filteredVals = vals.filter(
                    (val) => !arr.includes(val?.id),
                  )

                  setSelect([])
                  onChange(variant === 'single' ? null : (filteredVals as any))
                }}
              >
                {select.length === 1
                  ? `Remove ${select.length} ${type}`
                  : `Remove ${select.length} ${type}s`}
              </Button>
            )}

            {value?.length > 0 && (
              <ContextMenu icon="more-horizontal">
                {/* <ContextMenu.Item
                  label="Expand"
                  leadIcon="external"
                  disabled={value?.length < 1}
                  onClick={() => setExpandedView(true)}
                />
                <ContextMenu.Separator /> */}
                <ContextMenu.Item
                  label="Remove all"
                  leadIcon="close"
                  color="red"
                  onClick={removeAllRefsDialog}
                />
              </ContextMenu>
            )}
          </Stack>
        )}
      </div>
    </>
  )
}
